<template>
  <div class="SignIn">
    <div class="SignIn-container">
      <div class="SignIn-title">{{ $t('signIn.title') }}</div>
      <div class="SignIn-subtitle">{{ $t('signIn.subtitle') }}</div>
      <ValidationObserver v-slot="{ handleSubmit }" ref="observer">
        <form @submit.prevent="handleSubmit(initUser)">
          <v-text-field data-e2e="ecpIdInput" :value="ecpId" type="text" :label="$t('signIn.essilorIdLabel')" readonly>
            <template v-slot:append> </template>
          </v-text-field>
          <v-text-field
            data-e2e="signInEmailInput"
            :value="email"
            type="email"
            :label="$t('signIn.emailLabel')"
            readonly
          />
          <ValidationProvider
            name="passwordName"
            rules="required|min:8"
            v-slot="{ failedRules, dirty }"
            mode="aggressive"
          >
            <v-text-field
              v-model="password"
              @click:append="showPassword = !showPassword"
              @input="inputChange"
              :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
              data-e2e="signInPasswordInput"
              :error-messages="failedRules.required"
              :label="$t('signIn.passwordLabel')"
              :type="showPassword ? 'text' : 'password'"
            />
            <div
              class="SignIn-passwordError mt-3"
              :class="{ 'red--text': failedRules.min }"
              data-e2e="signInPasswordErrorMessageMinChar"
            >
              <v-icon left small :color="getColor(failedRules.min, dirty)">mdi-check-circle</v-icon
              >{{ $t('signIn.passwordError.min', { value: 8 }) }}
            </div>
            <div
              class="SignIn-passwordError"
              :class="{ 'red--text': getColor(notSpecialChar, dirty) === 'error' }"
              data-e2e="signInPasswordErrorMessageSpecialChar"
            >
              <v-icon left small :color="getColor(notSpecialChar, dirty)">mdi-check-circle</v-icon
              >{{ $t('signIn.passwordError.specialCharacter', { value: 1 }) }}
            </div>
            <div
              class="SignIn-passwordError"
              :class="{ 'red--text': getColor(notNumber, dirty) === 'error' }"
              data-e2e="signInPasswordErrorMessageNotNumber"
            >
              <v-icon left small :color="getColor(notNumber, dirty)">mdi-check-circle</v-icon
              >{{ $t('signIn.passwordError.number', { value: 1 }) }}
            </div>
          </ValidationProvider>
          <validation-provider name="CGU" :rules="{ required: { allowFalse: false } }" v-slot="{ errors }">
            <TermsOfServices v-model="acceptTerms" :errors="errors" />
          </validation-provider>
          <v-btn
            title="test"
            type="submit"
            class="SignIn-submitButton mt-6 text-none"
            color="primary"
            data-e2e="signInSubmitButton"
            depressed
            >{{ $t('signIn.submitButton') }}</v-btn
          >
        </form>
      </ValidationObserver>
    </div>

    <div class="SignIn-actionsContainer">
      <v-alert v-if="alreadyExists" type="error" elevation="2">
        {{ $t('signIn.alreadyExists') }}
      </v-alert>
      <v-btn
        @click="$emit('login')"
        class="SignIn-loginButton text-none"
        color="text-color"
        data-e2e="goToLoginButton"
        small
        text
        >{{ $t('signIn.loginButton') }}</v-btn
      >
      <v-menu v-model="helpEcpId" :close-on-content-click="false" :nudge-width="200" offset-y>
        <template v-slot:activator="{ on }">
          <v-icon v-on="on" class="SignIn-ecpIdInfos">mdi-help-circle-outline</v-icon>
        </template>
        <v-card class="px-4 py-3" elevation="5">
          <div class="px-2 py-1 text-center secondary--text">
            <span>{{ $t('signIn.help.1') }}</span>
            <br />
            <span class="font-weight-bold">{{ $t('signIn.help.2') }}</span>
          </div>
        </v-card>
      </v-menu>
    </div>
  </div>
</template>

<script>
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import gql from 'graphql-tag'

import auth from '@/services/auth'
import { initVueGtm } from '@/services/gtm'
import TermsOfServices from '@/components/login/UI/TermsOfServices'

const INIT_USER = gql`
  mutation initUser($user: InitUserInput!) {
    initUser(user: $user) {
      ecpId
      shops {
        country
      }
    }
  }
`

export default {
  name: 'SignIn',

  components: {
    ValidationObserver,
    ValidationProvider,
    TermsOfServices
  },

  data() {
    return {
      acceptTerms: false,
      alreadyExists: false,
      helpEcpId: false,
      password: '',
      showPassword: false
    }
  },

  computed: {
    email() {
      return this.$route.query.email
    },
    ecpId() {
      return this.$route.query.ecpId
    },
    userRole() {
      if (this.$route.query.role && this.$route.query.role.toUpperCase() === 'COUNTRYMANAGER') return 'countryManager'
      return 'owner'
    },
    notNumber() {
      const format = /[0-9]/
      return !format.test(this.password)
    },
    notSpecialChar() {
      const format = /[ `!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~]/
      return !format.test(this.password)
    },
    token() {
      return this.$route.query.token
    },
    userUuid() {
      return this.$route.query.userUuid
    }
  },

  methods: {
    getColor(failed, dirty) {
      if (!dirty) return ''
      return failed ? 'error' : 'success'
    },
    async initUser() {
      
      if (this.notNumber || this.notSpecialChar) return
     
      if (this.$store.state.myUser && this.$store.state.myUser.ecpId !== this.ecpId) {
        await auth.logout()
      }

      try {
        const { data } = await this.$apollo.mutate({
          mutation: INIT_USER,
          variables: {
            user: {
              ecpId: this.ecpId,
              email: this.email,
              password: this.password,
              token: this.token,
              role: this.userRole
            }
          }
        })
        if (this.$get(data, 'initUser.shops[0].country')) {
          initVueGtm(data.initUser.shops[0].country)

          this.$gtm.trackEvent({
            event: 'Sign_up',
            essilor_client_id: data.initUser.ecpId,
            practitioner_id: data.initUser.ecpId,
            shopUuid: data.initUser.shops[0].shopUuid
          })
        }

        this.$emit('login', true)
      } catch (e) {
        if (e.graphQLErrors[0]?.extensions?.exception?.errorKey === 'user.alreadyExists') {
          this.alreadyExists = true
        }
      }
    },
    inputChange(value) {
      value === '' && this.$nextTick(() => this.$refs.observer.reset())
    }
  }
}
</script>

<style scoped lang="scss">
.SignIn {
  text-align: left;
  color: var(--v-secondary-base);
}

.SignIn-ecpIdInfos {
  cursor: pointer;
}

.SignIn-title {
  font-size: 1.5rem;
  font-weight: 600;
}

.SignIn-subtitle {
  color: var(--v-secondary-base);
  font-weight: 300;
  margin-bottom: 40px;
}

.SignIn-passwordError {
  text-align: left;
  font-size: 0.8em;
}

.SignIn-submitButton {
  display: block;
  margin: 0 auto;
}

.SignIn-actionsContainer {
  position: fixed;
  top: 12px;
  right: 16px;
}

.SignIn-loginButton {
  text-decoration: underline;
}

.SignIn-termsLink {
  color: var(--v-primary-base);
}
</style>
